import axios, { AxiosResponse } from 'axios';
import { IAction, IObjectSignature, IOperation } from 'types';
import { put, select, call } from 'typed-redux-saga';
import {
    getOmjsProjectInstance,
    getCustomWidgetSettings,
    getOmjsViewInstance,
    getCachedFullContainers,
    getOperationData,
    getOmjsContainer,
    getChildren
} from 'selectors';
import { searchAddressesYandex } from 'api/geocoder-yandex';
import { fuzzyMatch } from 'utils';
import i18next from 'i18next';
import {
    Service,
    bindActionsTyped,
    every,
    leading, latest
} from '../service-utils';
import { operation, OperationsService } from '../operations/operations-service';
import { searchActions } from './search-reducer-actions';
import { IObjectSearchResult, IPKKObjectSearchResult, SearchType } from './search-reducer';
import { searchSitesPkk } from '../../api/app';
import { pkkSearchByQuery } from '../../api/pkk';

export type SubmitApplicationOverlayOperation = IOperation<{visible: boolean, objects: IObjectSignature[];}>;
export type MapLinkOverlayOperation = IOperation<{visible: boolean, link: string;}>;
export type ObjectLinkOverlayOperation = IOperation<{visible: boolean, link: string;}>;
export type FavoritesExportLayerChooserOperation = IOperation<{visible: boolean;}>;

class Search extends Service {
    static serviceName = 'SearchService';
    static generatedStopActionTypes: string[] = [];
    static generatedWatchers: Function[] = [];

    @every
    static* setSearchInputValue(action: IAction<string>) {
        yield* put(searchActions.setSearchInputValue(action.payload));
    }

    @leading
    @operation('SEARCH_OBJECTS_OMS')
    static* searchPkkObjects(action: IAction<string>) {
        let search_string = action.payload;

        let search_settings = yield* select(getCustomWidgetSettings, 'search');
        const containers = yield* select((state) => getCachedFullContainers(state));
        const sites = containers
            .filter(c => [ 'sites_1', 'sites_2', 'sites_3', 'sites_4' ].includes(c.get('code')))
            .reduce((acc, c) => ({ ...acc, [c.get('code')]: c }), {});
        let project = yield* select(getOmjsProjectInstance);
        let { mapCode } = project.options;
        const { data: objects } = yield* call(
            searchSitesPkk,
            search_string,
            mapCode,
            search_settings.values.searchLimit ? parseInt(search_settings.values.searchLimit) : 5
        );
        const patched = objects.map(o => ({
            orbis_id: o.id,
            type: 'object',
            layer: o.fields.layer,
            layer_name: sites[o.fields.layer]?.get('name') || o.fields.layer,
            layer_icon: sites[o.fields.layer]?.get('icon') || '',
            search_column: 'cadastral_land_number_maps_ru',
            value: o.fields.cadastral_land_number_maps_ru,
            name_column: 'name',
            name: o.fields.name || sites[o.fields.layer]?.get('name')
        }));
        yield* put(searchActions.setOmsSearchResults(patched));
    }

    @leading
    @operation('SEARCH_OBJECTS_OMS')
    static* searchObjects(action: IAction<string>) {
        let search_string = action.payload;
        let search_settings = yield* select(getCustomWidgetSettings, 'search');
        let project = yield* select(getOmjsProjectInstance);
        let searchObjectsUrl = `${project.options.apiUrl}${project.options.mapCode}/search/`;
        let data = {
            'phrase': search_string,
            'limit': search_settings.values.searchLimit ? parseInt(search_settings.values.searchLimit) : 5
        };
        if (project.token) {
            data['token'] = project.token;
        }
        if (project.lng) {
            // для поискового запроса именно lang, а не lng
            data['lang'] = project.lng;
        }

        let search_results: AxiosResponse<IObjectSearchResult[]> = yield* call(
            async () => axios.get(searchObjectsUrl, { params: data })
        );

        let result = search_results.data.filter(c => c.type === 'object');

        yield* put(searchActions.setOmsSearchResults(result));
    }

    @leading
    @operation('SEARCH_OBJECTS_OMS')
    static* searchAddressOMS(action: IAction<string>) {
        let search_string = action.payload;
        let project = yield* select(getOmjsProjectInstance);

        throw new Error('Not implemented');
    }

    @leading
    @operation('SEARCH_OBJECTS_OMS')
    static* searchAddressYandex(action: IAction<string>) {
        let search_string = action.payload;
        let search_settings = yield* select(getCustomWidgetSettings, 'search');
        let view = yield* select(getOmjsViewInstance);
        let ss = search_settings.values;
        let yandex_limit: any = { bbox: '', rspn: 0 };

        if (
            ss.searchYandexLowerLeftCorner
            && ss.searchYandexUpperRightCorner
        ) {
            yandex_limit.bbox = `${ss.searchYandexLowerLeftCorner}~${ss.searchYandexUpperRightCorner}`;
            yandex_limit.rspn = 1;
        } else {
            yandex_limit = {};
        }

        let search_results = yield* call(searchAddressesYandex, {
            'geocode': search_string,
            'format': 'json',
            'results': search_settings.values.searchLimit ? parseInt(search_settings.values.searchLimit) : 5,
            'apikey': view.options.yandex_api_key,
            ...yandex_limit
        });

        yield* put(searchActions.setOmsSearchResults(
            search_results.data?.response?.GeoObjectCollection?.featureMember ?? []
        ));
    }

    @leading
    static* searchAddress(action: IAction<string>) {
        let search_settings = yield* select(getCustomWidgetSettings, 'search');
        if (search_settings?.values?.searchYandexApi) {
            yield* Search.searchAddressYandex(action);
        } else {
            yield* Search.searchAddressOMS(action);
        }
    }

    @leading
    @operation('SEARCH_OBJECTS_OMS')
    static* searchLayer(action: IAction<string>) {
        let pub_containers: omjs.IContainer[] = yield* select(getOperationData, 'CONTAINERS');
        let containers = pub_containers
            .map(cont => ({ ...cont, parentName: '' }))
            .filter(cont => cont.type !== 'folder')
            .filter((cont) => {
                // Убираем вложенные слои (слои у которых parent это слой)
                let parent_cont = pub_containers.find(pub_cont => pub_cont.id === cont.parent_id);
                if (parent_cont && parent_cont.type === 'layer') {
                    return false;
                } else {
                    cont.parentName = parent_cont.name;
                    return true;
                }
            })
            .filter((cont) => {
                /* Не показываем слои генпланов */
                if (i18next.language === 'ru') {
                    let gen_plans_container = getOmjsContainer(false, 'gen_plans');
                    let gen_plans_children = getChildren(false, gen_plans_container.get('id'));
                    let is_gen_plans_children = !!gen_plans_children.find(c => c.code === cont.code);
                    return !is_gen_plans_children;
                } else {
                    return true;
                }
            })
            // .filter((cont) => {
            //     if (cont.type === 'layer') {
            //         let layer_config = getOmjsLayerConfig(false, cont.code);
            //         if (layer_config) {.
            //             return (
            //                 // layer_config.objectsListEnabled
            //                 layer_config.balloonEnabled
            //                 || layer_config.objectWindowEnabled
            //             );
            //         } else {
            //             return false;
            //         }
            //     } else {
            //         return true;
            //     }
            // })
            .filter(cont => (fuzzyMatch(action.payload, cont.name) || fuzzyMatch(action.payload, cont.parentName)));

        yield* put(searchActions.setOmsSearchResults(containers));
    }

    @latest
    @operation('SEARCH_OBJECTS_OMS')
    static* searchPkk(action: IAction<string>) {
        const { data } = yield* call(pkkSearchByQuery, { query: action.payload });
        const result = data.features
            .sort((a, b) => (((a.properties?.cadastral_number || '') as string).match(/:/g) || []).length
                - (((b.properties?.cadastral_number || '') as string).match(/:/g) || []).length)
            .map(f => ({
                name: f.properties?.name || f.properties?.cadastral_number,
                cadastral_number: f.properties?.cadastral_number,
                geom: f.geometry
            } as IPKKObjectSearchResult));
        yield* put(searchActions.setOmsSearchResults(result));
    }

    @every
    static* clearState() {
        yield* put(searchActions.clearState());
        yield* OperationsService.clearOperation({ type: '_', payload: 'SEARCH_OBJECTS_OMS' });
    }

    @every
    static* setSearchType(action: IAction<SearchType>) {
        yield* OperationsService.clearOperation({ type: '_', payload: 'SEARCH_OBJECTS_OMS' });
        yield* put(searchActions.setSearchType(action.payload));
    }

    @latest
    static* performSearch(action: IAction<{ type: string, value: string }>) {
        const { type, value } = action.payload;
        switch (type) {
            case 'objects': {
                if (/^\d*:/.test(value)) {
                    yield call(Search.searchPkkObjects, { type: 'searchPkkObjects', payload: value });
                } else {
                    yield call(Search.searchObjects, { type: 'searchObjects', payload: value });
                }
                break;
            }
            case 'address': {
                yield call(Search.searchAddress, { type: 'searchAddress', payload: value });
                break;
            }
            case 'layers': {
                yield call(Search.searchLayer, { type: 'searchLayer', payload: value });
                break;
            }
            case 'pkk': {
                yield call(Search.searchPkk, { type: 'searchPkk', payload: value });
                break;
            }
            default: break;
        }
    }
}


export const searchService: Search = new Search();
export const actions = bindActionsTyped(Search, searchService);
searchService.actions = actions;
